import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import AdminLayout from "@/components/Layout";
import { Radio, Users, Activity, ShieldCheck, Plus, Trash2, Copy, Check, ListVideo } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function StatCard({ icon: Icon, label, value, tone = "amber" }) {
  const toneCls = {
    amber: "text-amber-500 bg-amber-500/10",
    green: "text-green-500 bg-green-500/10",
    blue:  "text-sky-400 bg-sky-400/10",
    red:   "text-red-500 bg-red-500/10",
  }[tone] || "text-amber-500 bg-amber-500/10";

  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5 flex items-center gap-4">
      <div className={`w-11 h-11 rounded-lg grid place-items-center ${toneCls}`}>
        <Icon size={20} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-mono uppercase tracking-widest text-gray-400">{label}</div>
        <div className="text-2xl font-bold text-white">{value}</div>
      </div>
    </div>
  );
}

export default function Lines() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/lines")
      .then((res) => {
        setData(Array.isArray(res.data) ? res.data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };
  useEffect(load, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Bu hattı silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/lines/${id}`);
      setData((rows) => rows.filter((l) => l._id !== id));
    } catch {
      window.alert("Silinemedi.");
    }
  };

  const m3uUrl = (id) => `${BACKEND_URL}/api/playlist/${id}.m3u`;

  const copyM3u = async (id) => {
    const url = m3uUrl(id);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1800);
    } catch {
      // Fallback: prompt with the URL so the user can copy manually.
      window.prompt("Linki kopyalayın:", url);
    }
  };

  const total = data.length;
  const activeCount = data.filter((l) => l.status === "active").length;
  const connections = data.reduce((sum, l) => sum + (l.active_connections || 0), 0);
  const customers = new Set(data.map((l) => l.customer_id).filter(Boolean)).size;

  const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("tr-TR"); } catch { return iso; }
  };

  return (
    <AdminLayout breadcrumb={["Operasyon", "Hatlar"]} title="Hatlar">
      <div className="p-6" data-testid="lines-page">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-white">Hatlar</h1>
          <button
            className="bg-amber-500 text-black px-4 py-2 rounded-lg font-bold flex items-center gap-2 transition-transform active:scale-95"
            data-testid="lines-create-btn"
          >
            <Plus size={20} /> Yeni Hat
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard icon={Radio}       label="Toplam Hat"     value={total} />
          <StatCard icon={ShieldCheck} label="Aktif"          value={activeCount} tone="green" />
          <StatCard icon={Activity}    label="Canlı Bağlantı" value={connections} tone="blue" />
          <StatCard icon={Users}       label="Müşteri Sayısı" value={customers} />
        </div>

        <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
          <table className="w-full text-left text-white">
            <thead className="bg-slate-950 text-gray-400 text-xs uppercase font-bold">
              <tr>
                <th className="p-4">Kullanıcı Adı</th>
                <th className="p-4">Paket</th>
                <th className="p-4">Bağlantı</th>
                <th className="p-4">Bitiş</th>
                <th className="p-4">Son IP</th>
                <th className="p-4">Durum</th>
                <th className="p-4 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {loading ? (
                <tr><td colSpan="7" className="p-8 text-center text-gray-500 font-medium">Yükleniyor...</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan="7" className="p-8 text-center text-gray-500 font-medium">Hat bulunamadı.</td></tr>
              ) : (
                data.map((l) => {
                  const statusTone =
                    l.status === "active"  ? "bg-green-500/10 text-green-500" :
                    l.status === "expired" ? "bg-red-500/10 text-red-500"     :
                                             "bg-amber-500/10 text-amber-500";
                  return (
                    <tr key={l._id} className="hover:bg-slate-800/30 transition-colors" data-testid={`line-row-${l._id}`}>
                      <td className="p-4"><div className="font-mono text-sm">{l.username}</div></td>
                      <td className="p-4 text-gray-300 text-sm">{l.package_name || "—"}</td>
                      <td className="p-4 font-mono text-sm text-gray-300">
                        {(l.active_connections ?? 0)} / {l.max_connections ?? 1}
                      </td>
                      <td className="p-4 text-gray-400 font-mono text-sm">{fmt(l.expiry_date)}</td>
                      <td className="p-4 text-gray-500 font-mono text-xs">{l.last_ip || "—"}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${statusTone}`}>
                          {l.status}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => copyM3u(l._id)}
                            disabled={l.status !== 'active'}
                            className={`p-2 rounded-lg transition-colors flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide ${
                              l.status !== 'active'
                                ? 'text-gray-700 cursor-not-allowed'
                                : copiedId === l._id
                                  ? 'text-emerald-400 bg-emerald-500/10'
                                  : 'text-amber-400 hover:text-amber-300 hover:bg-amber-500/10'
                            }`}
                            title={l.status !== 'active' ? 'Yalnızca aktif hatlarda kullanılabilir' : 'M3U linkini kopyala'}
                            data-testid={`line-copy-m3u-${l._id}`}
                          >
                            {copiedId === l._id
                              ? <><Check size={14} /> Kopyalandı</>
                              : <><ListVideo size={14} /> M3U</>}
                          </button>
                          <button
                            onClick={() => handleDelete(l._id)}
                            className="text-red-500 hover:text-red-400 p-2 rounded-lg hover:bg-red-500/10 transition-colors"
                            title="Sil"
                            data-testid={`line-delete-${l._id}`}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
